import { useQuery } from '@tanstack/react-query';

import { Country, DetailsResponseItem, WoosmapType } from '../../types/commons';
import { getCurrentCountry } from '../../utils/country';

import { WithApiKey, WithPrivateKey } from './types';

export type AutocompleteResponseItem = {
  public_id: string;
  description: string;
  type: WoosmapType;
};

const woosmapCountry: Record<Country, string> = {
  FR: 'country:fr',
  GB: 'country:gb',
};

export const getKey = (options: WithApiKey | WithPrivateKey): Record<string, string> => {
  if ('apiKey' in options) return { key: options.apiKey };
  // In mobile we have to use privateKey and not public key
  return { private_key: options.privateKey };
};

type UseAddressQueryParams = (WithApiKey | WithPrivateKey) & {
  keyword: string;
};

export const useAddressQuery = ({ keyword, ...keys }: UseAddressQueryParams) =>
  useQuery<AutocompleteResponseItem[], Error>({
    // To avoid flickering of suggestions in the autocomplete address
    keepPreviousData: !!keyword,
    queryKey: ['address', keyword, keys],
    queryFn: async ({ signal }) => {
      const data = await fetch(
        `https://api.woosmap.com/localities/autocomplete/?${new URLSearchParams({
          input: keyword,
          components: woosmapCountry[getCurrentCountry()],
          types: 'address',
          ...getKey(keys),
        })}`,
        { signal },
      );
      const dataJson = await data.json();
      return dataJson.localities;
    },
    enabled: keyword.length > 4,
  });

type UseAddressDetailParams = (WithApiKey | WithPrivateKey) & {
  publicId?: string;
};
export const useAddressDetailQuery = ({ publicId, ...keys }: UseAddressDetailParams) => {
  const { data: addressDetail } = useQuery<DetailsResponseItem, Error>({
    queryKey: ['address_detail', publicId, keys],
    queryFn: async () => {
      const data = await fetch(
        `https://api.woosmap.com/localities/details?${new URLSearchParams({
          public_id: publicId || '',
          ...getKey(keys),
        })}`,
      );
      const dataJson = await data.json();
      return dataJson.result;
    },
    enabled: !!publicId,
  });
  return { addressDetail };
};
